// These modules are loaded lazily: static imports create a circular dependency
// (routing -> process -> authenticationService -> remote-start -> query-params -> routing) that breaks app start-up.
const loadAuth = () => Promise.all([import('utils/authenticationService'), import('common/global-store/api')])

const FALLBACK_REDIRECT_MS = 8000
const LOGIN_CLEANUP_KEY = 'sessionExpiredCleanupAt'
const LOGIN_CLEANUP_COOLDOWN_MS = 30000

let expiring = null

function isOnLoginPage() {
    return window.location.pathname.startsWith('/login')
}

// Prevents a reload loop on /login if the freshly created session is also rejected
function loginCleanupRecentlyDone() {
    try {
        return Date.now() - Number(sessionStorage.getItem(LOGIN_CLEANUP_KEY) || 0) < LOGIN_CLEANUP_COOLDOWN_MS
    } catch (e) {
        return false
    }
}

function remember(key, value) {
    try {
        sessionStorage.setItem(key, value)
    } catch (e) {
        // storage unavailable - not critical
    }
}

/**
 * Single entry point for "the server no longer accepts this session".
 * Idempotent: concurrent callers (e.g. a burst of failing API calls) share one sign-out.
 * Always clears stale credentials - including on /login, where a stored user would otherwise
 * stop the login form rendering - and guarantees the user ends up on a working login page
 * even if the normal sign-out flow fails or stalls.
 */
export function handleSessionExpired(reason) {
    if (expiring) return expiring

    const onLoginPage = isOnLoginPage()
    // Reload in place on /login so its URL (e.g. reset-password parameters) is preserved
    const forceRedirect = () => (onLoginPage ? window.location.reload() : window.location.replace('/login'))

    expiring = loadAuth()
        .then(async ([{ signOut }, { getCurrentAuthenticatedUser, clearCurrentAuthenticatedUser }]) => {
            if (onLoginPage && (!getCurrentAuthenticatedUser() || loginCleanupRecentlyDone())) {
                expiring = null
                return
            }

            console.warn('Session expired, signing out:', reason)
            if (onLoginPage) {
                remember(LOGIN_CLEANUP_KEY, String(Date.now()))
            } else {
                remember('intendedDest', `${window.location.pathname}${window.location.search}`)
            }

            const forceCleanRedirect = () => {
                clearCurrentAuthenticatedUser()
                forceRedirect()
            }
            // signOut normally navigates away; if it stalls, force it so the user is never left on a blank page
            setTimeout(forceCleanRedirect, FALLBACK_REDIRECT_MS)
            try {
                await signOut('/login')
            } catch (e) {
                console.error('Sign out failed during session expiry', e)
                forceCleanRedirect()
            }
        })
        .catch((e) => {
            console.error('Session expiry handling failed', e)
            forceRedirect()
        })
    return expiring
}
